
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const UsedCarsPage = () => {
  useEffect(() => {
    // SEO設定
    document.title = '中古車販売・買取・輸出 | 株式会社伊藤総業 - 東京都世田谷区';
    
    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "中古車販売・買取・輸出 - 株式会社伊藤総業",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/used-cars`,
      "description": "株式会社伊藤総業の中古車事業。高品質な中古車の販売から買取、海外輸出まで自動車に関する総合的なサービスを提供。",
      "mainEntity": {
        "@type": "Service",
        "name": "中古車販売・買取・輸出",
        "provider": {
          "@type": "Organization",
          "name": "株式会社伊藤総業"
        },
        "serviceType": "中古車販売",
        "areaServed": {
          "@type": "Place",
          "name": "東京都世田谷区"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Header />
      
      {/* Hero Section */}
      <main>
        <section 
          className="relative h-96 flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/c8e74595f48f7e7d3692968bc3567a54.jpg')`
          }}
        >
          <div className="container mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              中古車販売・買取・輸出
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              高品質な中古車の販売から買取、海外輸出まで<br />
              自動車に関する総合的なサービスを提供
            </p>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">サービス内容</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                お客様のカーライフを総合的にサポートする幅広いサービスをご提供しています
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-car-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">中古車販売</h3>
                <p className="text-gray-600">
                  厳選された高品質な中古車を豊富に取り揃え、お客様のニーズに合った車両をご提案いたします。
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">車両買取</h3>
                <p className="text-gray-600">
                  適正価格での車両買取を行い、お客様の大切な愛車を正当に評価いたします。
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-ship-line text-purple-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">海外輸出</h3>
                <p className="text-gray-600">
                  日本の高品質な中古車を海外市場へ輸出し、グローバルなニーズにお応えしています。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">選ばれる理由</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-shield-check-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">品質保証</h4>
                      <p className="text-gray-600">
                        専門スタッフによる厳格な品質チェックを実施し、安心してお乗りいただける車両のみを販売しています。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-price-tag-3-line text-green-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">適正価格</h4>
                      <p className="text-gray-600">
                        市場価格を徹底調査し、お客様にとって最適な価格でのご提案を心がけています。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-customer-service-2-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">充実サポート</h4>
                      <p className="text-gray-600">
                        購入後のアフターサービスも充実しており、長期にわたってお客様をサポートいたします。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-96 bg-cover bg-center rounded-xl" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20car%20inspection%20process%2C%20automotive%20technician%20checking%20vehicle%20quality%2C%20car%20maintenance%20and%20inspection%2C%20professional%20automotive%20service%2C%20clean%20garage%20environment&width=600&height=400&seq=car-inspection&orientation=landscape')`
              }}></div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">ご利用の流れ</h2>
              <p className="text-xl text-gray-600">
                簡単なステップでスムーズなお取引を実現
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">お問い合わせ</h3>
                <p className="text-gray-600">
                  お電話またはお問い合わせフォームからご連絡ください
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">ご相談・査定</h3>
                <p className="text-gray-600">
                  ご希望をお聞きし、最適なプランをご提案いたします
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">契約・手続き</h3>
                <p className="text-gray-600">
                  必要な書類の準備と各種手続きをサポートします
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">お引き渡し</h3>
                <p className="text-gray-600">
                  車両の最終チェック後、お客様にお引き渡しいたします
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              中古車のことなら何でもご相談ください
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              販売・買取・輸出に関するご質問やご相談は、お気軽にお問い合わせください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                お問い合わせフォーム
              </Link>
              <a 
                href="tel:070-4228-3984" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                070-4228-3984
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UsedCarsPage;
